import { camelCase, findLast, upperFirst } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DOCUMENTS_SIDES,
  GUARANTOR_DOCS_DESCRIPTIONS,
  USERS_TYPES,
} from '../../../shared/constants';
import useGuarantorDocuments from '../../../shared/hooks/useGuarantorDocuments';
import { IGuarantor } from '../../../shared/ts/guarantor';
import DocumentField from '../../DocumentField';
import { StyledH3 } from '../AddATenant/AddATenant.styles';
import { filterRequiredMissingDocumentForGuarantor } from './utils/requiredMissingDocumentsForGuarantor';

const UploadGuarantorDocuments = ({
  index,
  guarantor,
  occupation,
  handleMissingGuarantorDocument,
}: {
  index: number;
  guarantor: IGuarantor;
  occupation: string;
  handleMissingGuarantorDocument: (
    index: number,
    missingFields: {
      label: string;
      fields: { label: string; sideLabel?: string }[];
    },
  ) => void;
}): JSX.Element => {
  const { t } = useTranslation(['common', 'documentsflow', 'form']);

  const guarantorId = Number(guarantor?.id);

  const guarantorOccupation = camelCase(guarantor?.occupation ?? occupation);
  const guarantorDocuments = guarantor?.documents;

  const isLoadingGuarantorDocuments = useGuarantorDocuments(guarantorId);

  const identityDocument = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'identity_document',
      })
    : undefined;
  const taxNumber = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'tax_number',
      })
    : undefined;
  const thirdLastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK,
      })
    : undefined;
  const secondLastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK,
      })
    : undefined;
  const lastPaycheck = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.LAST_PAYCHECK,
      })
    : undefined;
  const workContract = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.WORK_CONTRACT,
      })
    : undefined;
  const secondLastIncomeTaxReturn = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const lastIncomeTaxReturn = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const bankAccountBalance = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.BANK_ACCOUNT_BALANCE,
      })
    : undefined;
  const penultimateBankAccountBalance = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.PENULTIMATE_ACCOUNT_STATEMENT,
      })
    : undefined;
  const prepenultimateBankAccountBalance = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description:
          GUARANTOR_DOCS_DESCRIPTIONS.PREPENULTIMATE_ACCOUNT_STATEMENT,
      })
    : undefined;
  const lastPension = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.LAST_PENSION_SLIPS,
      })
    : undefined;
  const secondLastPension = guarantorDocuments
    ? findLast(guarantorDocuments, {
        type: 'proof_of_income',
        description: GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PENSION_SLIPS,
      })
    : undefined;

  useEffect(() => {
    const missingValue = filterRequiredMissingDocumentForGuarantor({
      index,
      occupation: guarantor?.occupation ?? occupation,
      guarantorDocuments,
    });
    handleMissingGuarantorDocument(index, missingValue ?? []);
  }, [occupation, guarantorDocuments]);
  return (
    <>
      {!isLoadingGuarantorDocuments && (
        <>
          <StyledH3>
            {upperFirst(
              guarantor?.occupation !== USERS_TYPES.FOREIGN_STUDENT
                ? t('idCardOrPassport')
                : t('passport'),
            )}
          </StyledH3>{' '}
          <br />
          <DocumentField
            documentSide={DOCUMENTS_SIDES.FRONT}
            title={`${upperFirst(t('identityDocument'))} (${t('front')})`}
            type="identity_document"
            documentUploaded={identityDocument}
            ownerId={guarantorId}
            isGuarantor
          />
          <DocumentField
            documentSide={DOCUMENTS_SIDES.REAR}
            title={`${upperFirst(t('identityDocument'))} (${t('rear')})`}
            type="identity_document"
            documentUploaded={identityDocument}
            ownerId={guarantorId}
            isGuarantor
          />
          <br />
          <StyledH3>{upperFirst(t('fiscalCode'))}</StyledH3> <br />
          <DocumentField
            title={upperFirst(t('fiscalCode'))}
            type="tax_number"
            documentUploaded={taxNumber}
            ownerId={guarantorId}
            isGuarantor
          />
          <br />
          {guarantorOccupation === USERS_TYPES.SELF_EMPLOYED && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:last2taxReturn'))}</StyledH3> <br />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:lastIncomeTaxReturn'))}
                type="proof_of_income"
                documentUploaded={lastIncomeTaxReturn}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:secondLastIncomeTaxReturn'))}
                type="proof_of_income"
                documentUploaded={secondLastIncomeTaxReturn}
                ownerId={guarantorId}
                isGuarantor
                description={
                  GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN
                }
              />
            </>
          )}
          {(guarantorOccupation === USERS_TYPES.ITALIAN_STUDENT ||
            guarantorOccupation === USERS_TYPES.EMPLOYEE) && (
            <>
              {' '}
              <br />
              <StyledH3>{upperFirst(t('form:workContract'))}</StyledH3>
              <br />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:workContract'))}
                type="proof_of_income"
                documentUploaded={workContract}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.WORK_CONTRACT}
              />
              <br />
              <StyledH3>{upperFirst(t('form:last3Paychecks'))}</StyledH3>
              <br />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:lastPaycheck'))}
                type="proof_of_income"
                documentUploaded={lastPaycheck}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.LAST_PAYCHECK}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:secondLastPaycheck'))}
                type="proof_of_income"
                documentUploaded={secondLastPaycheck}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:thirdLastPaycheck'))}
                type="proof_of_income"
                documentUploaded={thirdLastPaycheck}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK}
              />
            </>
          )}
          {guarantorOccupation === USERS_TYPES.RETIRED ||
            (guarantorOccupation === USERS_TYPES.PENSIONER && (
              <>
                <StyledH3>{upperFirst(t('form:last2PensionSlips'))}</StyledH3>
                <DocumentField
                  documentSide={DOCUMENTS_SIDES.FRONT}
                  title={upperFirst(t('form:lastPensionSlip'))}
                  type="proof_of_income"
                  documentUploaded={lastPension}
                  ownerId={guarantorId}
                  isGuarantor
                  description={GUARANTOR_DOCS_DESCRIPTIONS.LAST_PENSION_SLIPS}
                />
                <DocumentField
                  documentSide={DOCUMENTS_SIDES.FRONT}
                  title={upperFirst(t('form:penultimatePensionSlip'))}
                  type="proof_of_income"
                  documentUploaded={secondLastPension}
                  ownerId={guarantorId}
                  isGuarantor
                  description={
                    GUARANTOR_DOCS_DESCRIPTIONS.SECOND_LAST_PENSION_SLIPS
                  }
                />
                <br />
              </>
            ))}{' '}
          {guarantorOccupation === USERS_TYPES.SELF_EMPLOYED && (
            <>
              {' '}
              <br />
              <StyledH3>{upperFirst(t('form:bankAccountBalance'))}</StyledH3>
              <br />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:latestAccountStatement'))}
                type="proof_of_income"
                documentUploaded={bankAccountBalance}
                ownerId={guarantorId}
                isGuarantor
                description={GUARANTOR_DOCS_DESCRIPTIONS.BANK_ACCOUNT_BALANCE}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:penultimateAccountStatement'))}
                type="proof_of_income"
                documentUploaded={penultimateBankAccountBalance}
                ownerId={guarantorId}
                isGuarantor
                description={
                  GUARANTOR_DOCS_DESCRIPTIONS.PENULTIMATE_ACCOUNT_STATEMENT
                }
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:prepenultimateAccountStatement'))}
                type="proof_of_income"
                documentUploaded={prepenultimateBankAccountBalance}
                ownerId={guarantorId}
                isGuarantor
                description={
                  GUARANTOR_DOCS_DESCRIPTIONS.PREPENULTIMATE_ACCOUNT_STATEMENT
                }
              />
              <br />
            </>
          )}{' '}
        </>
      )}
    </>
  );
};

export default UploadGuarantorDocuments;
